/**
 * User-facing copy for backend error codes returned by the position
 * endpoints. The backend response body is `{ error, code }`; we look up
 * `code` here. Unknown codes fall back to `error`.
 */

import { readClientFormErrorCode, resolveClientFormError } from "@/utils/clientFormErrors";

/** The generic submit failure. The action already says Submit Failed, then
 *  Retry, so the form shows no line for it; only a specific reason shows. */
export const DEFAULT_POSITION_SUBMIT_ERROR = "We couldn't submit this position. Please try again.";

export const POSITION_ALREADY_EXISTS_COPY = "This wallet is already registered. Manage the existing position instead.";

export const WALLET_ALREADY_REGISTERED_COPY =
    "This wallet already belongs to another company recipient. Use a different wallet or contact Solomon support.";

/** A submit rejected because the address is registered already (to this
 *  Company Recipient or another): the action reads Already Registered, then
 *  Add New Address. */
export function isAlreadyRegisteredCopy(message: string | null): boolean {
    return message === POSITION_ALREADY_EXISTS_COPY || message === WALLET_ALREADY_REGISTERED_COPY;
}

const POSITION_ERROR_COPY: Record<string, string> = {
    CHALLENGE_EXPIRED: "Your ownership check expired. Verify again to continue.",
    CHALLENGE_CONSUMED: "Your ownership check was already used. Verify again to continue.",
    CHALLENGE_NOT_FOUND: "Your ownership check no longer matches this position. Verify again to continue.",
    SIGNATURE_INVALID: "We couldn't accept the wallet verification. Verify again to continue.",
    POSITION_ALREADY_EXISTS: POSITION_ALREADY_EXISTS_COPY,
    WALLET_ALREADY_REGISTERED: WALLET_ALREADY_REGISTERED_COPY,
    MULTISIG_NOT_FOUND:
        "We couldn't find a Squads multisig configuration at this address. If you copied a Treasury vault address, paste the Squads URL instead.",
    MULTISIG_LOOKUP_FAILED: "Couldn't reach the network to verify this multisig. Try again in a moment.",
    SIGNER_NOT_MULTISIG_MEMBER: "This wallet isn't a member of the multisig.",
    SIGNER_LACKS_VERIFY_PERMISSION: "This wallet can't verify the multisig. It needs Vote or Execute permission.",
    UNSUPPORTED_MULTISIG_PROGRAM: "Only Squads V4 multisigs are supported right now.",
    MULTISIG_REQUIRES_SIGN_MESSAGE: "Multisig positions can only be verified by signing a message.",
    INVALID_MULTISIG_SIGNER: "Signer address is invalid.",
    INVALID_SOLANA_ADDRESS: "Enter a valid Solana wallet address.",
    INVALID_POSITION_SOURCE_KIND: "Something went wrong with your submission. Refresh and try again.",
    VERIFICATION_PROOF_REPLAYED: "This signature has already been used. Sign a fresh message.",
    WALLET_VERIFICATION_REQUIRED:
        "Position was not submitted. Go back to Verify and complete wallet verification first.",
    WALLET_VERIFICATION_FAILED:
        "We couldn't accept the wallet verification. It may have expired. Go back to the Verify step and verify again."
};

export function positionErrorCopy(code: string | null | undefined, fallback: string): string {
    if (code && POSITION_ERROR_COPY[code]) return POSITION_ERROR_COPY[code];
    if (code === "conflict") {
        if (
            /position already exists|active program position already exists|program position.*already exists/i.test(
                fallback
            )
        ) {
            return POSITION_ERROR_COPY.POSITION_ALREADY_EXISTS;
        }
        if (/belongs to another client/i.test(fallback)) {
            return POSITION_ERROR_COPY.WALLET_ALREADY_REGISTERED;
        }
    }
    // The backend reports proof rejections (expired micro-transfer, bad
    // signature) as a generic bad_request — match on the message, like the
    // conflict cases above.
    if (
        code === "bad_request" &&
        /wallet verification failed|wallet must be verified|wallet verification is required|verification signature is required/i.test(
            fallback
        )
    ) {
        if (!/wallet verification failed/i.test(fallback)) {
            return POSITION_ERROR_COPY.WALLET_VERIFICATION_REQUIRED;
        }
        return POSITION_ERROR_COPY.WALLET_VERIFICATION_FAILED;
    }
    return resolveClientFormError({ code, message: fallback }, "position", fallback);
}

export function positionSubmitErrorCopy(error: unknown): string {
    const fallback = error instanceof Error && error.message ? error.message : DEFAULT_POSITION_SUBMIT_ERROR;
    const code = readClientFormErrorCode(error);
    const copy = positionErrorCopy(code, fallback);
    if (copy !== fallback) return copy;
    return resolveClientFormError(error, "position", fallback);
}
